import { useEffect, useState } from "react";
import { fetchEvents, thumbUrl, type EventSummary } from "./api.js";
import { useAuth } from "./auth.js";
import { useRecording } from "./recording.js";
import { useRoute, navigate, goBack } from "./router.js";
import { dateLabel, isDraft } from "./shared.js";
import { LooseView } from "./loose-view.js";
import { EventView } from "./album-view.js";

/**
 * L'aiguillage des écrans et la grille d'accueil.
 *
 * `Gallery` traduit la route en écran ; chaque écran vit dans son propre fichier
 * (`loose-view`, `album-view`, `photo-viewer`), et les briques communes dans `shared`.
 */
export function Gallery() {
  const route = useRoute();
  const { noteView } = useRecording();
  const albumId = route.kind === "album" ? route.id : null;

  // Alimente la timeline d'attribution : quel événement est affiché. Ne dépend que
  // de l'album — sinon changer de photo rejouerait noteView(album, null) après que
  // le viewer a posé (album, photo), et écraserait l'attribution à la photo.
  useEffect(() => {
    noteView(albumId, null);
  }, [albumId, noteView]);

  // « ‹ Albums » est un **retour** : il doit retrouver la position de défilement, comme
  // le bouton du navigateur. `goBack` retombe sur un remplacement d'URL si l'entrée
  // n'a pas été empilée par nous (arrivée directe par un lien partagé).
  if (route.kind === "loose") {
    return <LooseView photoId={route.photoId} onBack={() => goBack({ kind: "home" })} />;
  }
  if (route.kind === "album") {
    return (
      <EventView id={route.id} photoId={route.photoId} onBack={() => goBack({ kind: "home" })} />
    );
  }
  return (
    <AlbumGrid
      onOpen={(id) => navigate({ kind: "album", id, photoId: null })}
      onOpenLoose={() => navigate({ kind: "loose", photoId: null })}
    />
  );
}

function AlbumGrid({
  onOpen,
  onOpenLoose,
}: {
  onOpen: (id: string) => void;
  onOpenLoose: () => void;
}) {
  const { state } = useAuth();
  // Voir ce qui reste à trier et les groupes pas encore nommés ne demande aucun droit :
  // c'est une vue sur la collection. Ce sont les gestes de rangement qui sont réservés.
  const connecte = state.status === "authed";
  const [data, setData] = useState<{ events: EventSummary[]; looseCount: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchEvents()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) return <p className="gallery__error">Erreur : {error}</p>;
  if (!data) return <div className="gallery__loading">Chargement des albums…</div>;

  const { events, looseCount } = data;
  const named = events.filter((e) => !isDraft(e));
  const drafts = events.filter(isDraft);

  return (
    <div className="gallery" data-testid="album-grid">
      <h2 className="gallery__title">Albums <span className="gallery__count">{named.length}</span></h2>
      <div className="grid grid--albums">
        {connecte && looseCount > 0 && (
          <button className="card card--sort" onClick={onOpenLoose} data-testid="loose-card">
            <div className="card__img card__img--sort">À trier</div>
            <div className="card__meta">
              <span className="card__title">Photos à trier</span>
              <span className="card__sub">
                <span className="card__n">{looseCount}</span>
              </span>
            </div>
          </button>
        )}
        {named.map((e) => (
          <AlbumCard key={e.id} event={e} onOpen={onOpen} />
        ))}
      </div>

      {connecte && drafts.length > 0 && (
        <>
          <h2 className="gallery__title gallery__title--drafts">
            Brouillons <span className="gallery__count">{drafts.length}</span>
          </h2>
          <p className="loose__hint">
            Des groupes constitués mais pas encore nommés. Ouvre-en un et clique son titre
            pour le nommer — ou laisse la famille le raconter en session, Diapo écrira la
            description.
          </p>
          <div className="grid grid--albums">
            {drafts.map((e) => (
              <AlbumCard key={e.id} event={e} onOpen={onOpen} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AlbumCard({
  event,
  onOpen,
}: {
  event: EventSummary;
  onOpen: (id: string) => void;
}) {
  const draft = isDraft(event);
  return (
    <button
      className={`card ${draft ? "card--draft" : ""}`}
      onClick={() => onOpen(event.id)}
      data-testid={draft ? "draft-card" : "album-card"}
    >
      <div className="card__img">
        {event.coverPhotoId ? (
          <img src={thumbUrl(event.coverPhotoId)} alt="" loading="lazy" />
        ) : (
          <div className="card__placeholder" />
        )}
      </div>
      <div className="card__meta">
        <span className={`card__title ${draft ? "card__title--draft" : ""}`}>
          {draft ? "Sans titre" : event.title}
        </span>
        <span className="card__sub">
          {dateLabel(event) && <span className="card__date">{dateLabel(event)}</span>}
          <span className="card__n">{event.photoCount}</span>
        </span>
      </div>
    </button>
  );
}
